import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import styles from "./LandingPage.module.css";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

// Material Icons (same as in your sidebar)
import JoinInnerIcon from "@mui/icons-material/JoinInner";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CompareArrowsOutlinedIcon from "@mui/icons-material/CompareArrowsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import LocalAtmOutlinedIcon from "@mui/icons-material/LocalAtmOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import MailOutlineOutlinedIcon from "@mui/icons-material/MailOutlineOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import PriceCheckOutlinedIcon from "@mui/icons-material/PriceCheckOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import Person2OutlinedIcon from "@mui/icons-material/Person2Outlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import ProfileCard from "../../components/common/ProfileCard/ProfileCard";
import UnregisteredVendorProfileCard from "../../components/UnregisteredVendor/ProfileCard/UnregisteredVendorProfileCard";
import DashboardHeaderActions from "../../components/common/DashboardHeaderActions/DashboardHeaderActions";
import { CreditScoreOutlined } from "@mui/icons-material";
import MemorySharpIcon from "@mui/icons-material/MemorySharp";
import SettingsAccessibilityIcon from "@mui/icons-material/SettingsAccessibility";
import { SupervisorAccount } from "@mui/icons-material";

const ALWAYS_AVAILABLE_BUYER_MENU_IDS = [43, 44, 45, 46, 47, 49];

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Get user data from Redux store
  const data = useSelector((state) => state.loginData.value);

  const allowedMenu = useSelector(
    (state) => state.loginData.value?.allowed_menu || []
  );
  const normalizedAllowedMenu = Array.isArray(allowedMenu)
    ? allowedMenu
    : typeof allowedMenu === "string"
    ? (() => {
        try {
          const parsed = JSON.parse(allowedMenu);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      })()
    : [];

  // State for user roles
  const [roles, setRoles] = useState({
    SuperAdmin: false,
    Admin: false,
    Buyer: false,
    VendorAdmin: false,
    VendorContact: false,
    User: false,
    UnregisteredVendor: false,
  });

  // Determine user type and available menu items
  useEffect(() => {
    if (data?.access_type) {
      setRoles({
        SuperAdmin: ["superadmin", "superAdmin"].includes(
          data.access_type.toLowerCase()
        ),
        Admin: ["admin"].includes(data.access_type.toLowerCase()),
        Buyer: ["buyer"].includes(data.access_type.toLowerCase()),
        User: ["user"].includes(data.access_type.toLowerCase()),
        VendorAdmin: ["vendor admin", "vendoradmin"].includes(
          data.access_type.toLowerCase()
        ),
        VendorContact: ["vendor contact", "vendorcontact"].includes(
          data.access_type.toLowerCase()
        ),
        UnregisteredVendor: ["unregistered_vendor"].includes(
          data.access_type.toLowerCase()
        ),
      });
    }
  }, [data]);

  // Path prefixes
  const adminPathInitial = "/admin";
  const orgPathInitial = "/Organization_dashboard";
  const buyerPathInitial = "/Buyer_dashboard";
  const userPathInitial = "/User_dashboard";
  const vendorPathInitial = "/Vendor_dashboard";
  const grnpath = "/GRN_Dashboard";
  const isInventoryDesignation =
    data?.user?.designation === "Inventory Manager" ||
    data?.user?.designation === "Store Inventory";

  // Menu configurations (similar to your sidebar but simplified for cards)
  const superadminDashboard = [
    {
      title: "Customer Command Centre",
      icon: <Person2OutlinedIcon sx={{ fontSize: 40, color: "#1ee2e9ff" }} />,
      path: `${adminPathInitial}/buyer_access`,
      description:
        "Centralized dashboard to monitor and manage all customer activities and operations.",
    },
    {
      title: "Invite Buyer Admin",
      icon: <SupervisorAccount sx={{ fontSize: 40, color: "#b10fc0ff" }} />,
      path: `${adminPathInitial}/invitebuyeradmin`,
      description:
        "Invite and onboard buyer administrators to manage procurement activities",
    },
    {
      title: "AI Dialogue",
      icon: <SmartToyOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
      path: `${adminPathInitial}/Prompt`,
      description: "Manage AI dialogue prompts and configurations",
    },
    {
      title: "RFQ Category",
      icon: <CategoryOutlinedIcon sx={{ fontSize: 40, color: "#e91e63" }} />,
      path: `${adminPathInitial}/Prompt_creation`,
      description: "Configure RFQ categories and settings",
    },
    {
      title: "Use Logs ",
      icon: (
        <SettingsAccessibilityIcon sx={{ fontSize: 40, color: "#1a087eff" }} />
      ),
      path: `${adminPathInitial}/UserLogs`,
      description:
        "View and track system activity logs across users, buyers, vendors, and administrators",
    },
    {
      title: "API Usage Cost",
      icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 40, color: "#0b63a9" }} />,
      path: `${adminPathInitial}/AIUsageCost`,
      description:
        "View buyer-wise AI processor usage cost across accuracy modules.",
    },
  ];

  const orgDashboard = [
   
    {
      id: 18, // ✅ RFQ Automation
      title: "RFQ Events",
      icon: (
        <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />
      ),
      path: `${orgPathInitial}/RFQ`,
      description:
        "Automated RFQ creation, vendor selection, and dispatch—all in one click",
    },
    {
      id: 19, // ✅ PO Automation
      title: "Purchase Orders",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#ff9800" }} />,
      path: `${orgPathInitial}/PO`,
      description:
        "Automatically generate PO from approved quotations and selected vendors—ensuring speed and accuracy",
    },
    {
      id: 20, // ✅ Users
      title: "User Management",
      icon: <PeopleAltOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      path: `${orgPathInitial}/Buyers`,
      description: "Manage user accounts and permissions",
    },
    {
      id: 21, // ✅ Master Records
      title: "Master Records",
      icon: <InventoryOutlinedIcon sx={{ color: "#00acc1", fontSize: 40 }} />,
      path: `${orgPathInitial}/Items`,
      description:
        "Centralized, validated, and auto-updated Item and Supplier master records for accurate procurement and reporting",
    },
    {
      id: 22, // ✅ Accounting Codes
      title: "Accounting Codes",
      icon: <AccountTreeOutlinedIcon sx={{ fontSize: 40, color: "#8e24aa" }} />,
      path: `${orgPathInitial}/CostCenterTab`,
      description: "Manage cost centers, GL codes, and projects",
    },
    {
      id: 23, // ✅ Approver Range
      title: "Approver Range",
      icon: <LocalAtmOutlinedIcon sx={{ fontSize: 40, color: "#ff5722" }} />,
      path: `${orgPathInitial}/Slab`,
      description: "Configure approval ranges and workflows",
    },
     {
      title: "Procurement Command Center",
      icon: <DashboardOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
      path: `${orgPathInitial}/Dashboard`,
      description:
        "A real-time command center for tracking procurement activities, insights, and approvals.",
    }
  ];

  // const buyerDashboard = [
  //   {
  //     title: "Vendor Onboarding Automation",
  //     icon: <MailOutlineOutlinedIcon sx={{ fontSize: 40, color: "#ff5722" }} />,
  //     path: `${buyerPathInitial}/Invite`,
  //     description: "Digitally Verify, Validate and onboard Vendors in Minutes - no manual intervention",
  //   },
  //   {
  //     title: "Purchase Requisition",
  //     icon: <NoteAddOutlinedIcon sx={{ fontSize: 40, color: "#f44336" }} />,
  //     path: `${buyerPathInitial}/PR`,
  //     description: "Submit, Review, and Approve requests before initiating procurement",
  //   },
  //   {
  //     title: "RFQ Automation",
  //     icon: (
  //       <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />
  //     ),
  //     path: `${buyerPathInitial}/RFQ`,
  //     description: "Automated RFQ creation, vendor selection, and dispatch—all in one click",
  //   },
  //   {
  //     title: "Quick RFQ",
  //     icon: (
  //       <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#ff9800" }} />
  //     ),
  //     path: `${buyerPathInitial}/QuickRFQ`,
  //     description: "Instant RFQ generation for faster vendor responses",
  //   },
  //   {
  //     title: "PO Automation",
  //     icon: <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
  //     path: `${buyerPathInitial}/PO`,
  //     description: "Automatically generate PO from approved quotations and selected vendors—ensuring speed and accuracy",
  //   },
  //   {
  //     title: "Comparison Automation",
  //     icon: (
  //       <CompareArrowsOutlinedIcon sx={{ fontSize: 40, color: "#00acc1" }} />
  //     ),
  //     path: `${buyerPathInitial}/Comparison`,
  //     description: "Automatically compare technical specs and commercials across vendor quotes in minutes.",
  //   },

  //   {
  //     title: "GRN Automation",
  //     icon: <ChecklistOutlinedIcon sx={{ fontSize: 40, color: "#d81b60" }} />,
  //     path: `${grnpath}/GRN`,
  //     description: "Auto-generate Goods Receipt Notes from supplier invoices with real-time validation",
  //   },
  //   // {
  //   //   title: "MRP",
  //   //   icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 40, color: "#43a047" }} />,
  //   //   path: `${buyerPathInitial}/MRP`,
  //   //   description: "Material Requirements Planning",
  //   // },

  //   {
  //     title: "Trade Claims Automation",
  //     icon: <ReceiptOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />,
  //     path: `${userPathInitial}/Invoice`,
  //     description: "Auto-generate, e-sign, validate, and bulk invoice approval workflows with seamless ERP/payment integration",
  //   },
  //   {
  //     title: "Master Records",
  //     icon: <InventoryOutlinedIcon sx={{ fontSize: 40, color: "#8e24aa" }} />,
  //     path: `${buyerPathInitial}/Items`,
  //     description: "Centralized, validated, and auto-updated Item and Vendor master records for accurate procurement and reporting",
  //   },

  //   {
  //     title: "Dashboard",
  //     icon: <DashboardOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
  //     path: `${buyerPathInitial}/Dashboard`,
  //     description: "A real-time command center for tracking procurement activities, insights, and approvals",
  //   },
  // ];

  const profilePath = roles.Buyer
    ? "/Buyer_dashboard/My_profile"
    : roles.VendorAdmin || roles.VendorContact
    ? "/Vendor_dashboard/My_profile"
    : roles.UnregisteredVendor
    ? "/UnregisteredVendor_dashboard/My_profile"
    : "/Organization_dashboard/My_profile";

  const unregisteredVendorDashboard = [
    {
      title: "Price List",
      icon: (
        <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#7c3aed" }} />
      ),
      path: `/UnregisteredVendor_dashboard/catalog`,
      description: "Upload catalog/quotation and save extracted table data.",
    },
  ];

  const buyerDashboard = [
    {
      id: 12, // ✅ Vendor & Merchant Onboarding
      title: "Supplier Management",
      icon: <MailOutlineOutlinedIcon sx={{ fontSize: 40, color: "#ff5722" }} />,
      path: `${buyerPathInitial}/Invite`,
      description:
        "Digitally Verify, Validate and onboard Vendors in Minutes - no manual intervention",
    },
    {
      id: 12,
      title: "Unified Onboarding",
      icon: <HowToRegOutlinedIcon sx={{ fontSize: 40, color: "#2563eb" }} />,
      path: `${buyerPathInitial}/UnifiedOnboarding`,
      description:
        "Onboard suppliers, customers, dealers, distributors, service providers, and other business partners from one place",
    },
    {
      id: 2, // ✅ PR
      title: "Purchase Requisitions",
      icon: <NoteAddOutlinedIcon sx={{ fontSize: 40, color: "#f44336" }} />,
      path: `${buyerPathInitial}/PR`,
      description:
        "Submit, Review, and Approve requests before initiating procurement",
    },
    {
      id: 3, // ✅ RFQ
      title: "RFQ Events",
      icon: (
        <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />
      ),
      path: `${buyerPathInitial}/RFQ`,
      description:
        "Automated RFQ creation, vendor selection, and dispatch—all in one click",
    },
    {
      id: 4, // ✅ Quick RFQ
      title: "Xpress RFQ",
      icon: (
        <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#ff9800" }} />
      ),
      path: `${buyerPathInitial}/QuickRFQ`,
      description: "Instant RFQ generation for faster vendor responses",
    },
    {
      id: 43,
      title: "easyRFQ",
      icon: (
        <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#3b82f6" }} />
      ),
      path: `${buyerPathInitial}/FreeRFQ`,
      description:
        "Create and send an RFQ link using a mobile number and email, while keeping the same RFQ table experience.",
    },
    {
      id: 5, // ✅ PO
      title: "Purchase Orders",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      path: `${buyerPathInitial}/PO`,
      description:
        "Automatically generate PO from approved quotations and selected vendors—ensuring speed and accuracy",
    },
    {
      id: 44,
      title: "Sales Invoice",
      icon: <ReceiptOutlinedIcon sx={{ fontSize: 40, color: "#2563eb" }} />,
      path: `${buyerPathInitial}/SalesInvoice`,
      description:
        "Browse sales invoices, inspect line items, and review bank and amount details in a read-only register.",
    },
    {
      id: 6, // ✅ Comparison
      title: "Comparison Automation",
      icon: (
        <CompareArrowsOutlinedIcon sx={{ fontSize: 40, color: "#00acc1" }} />
      ),
      path: `${buyerPathInitial}/Comparison`,
      description:
        "Automatically compare technical specs and commercials across vendor quotes in minutes.",
    },
    {
      id: 10, // ✅ GRN
      title: "GRN Automation",
      icon: <ChecklistOutlinedIcon sx={{ fontSize: 40, color: "#d81b60" }} />,
      path: `${grnpath}/GRN`,
      description:
        "Auto-generate Goods Receipt Notes from supplier invoices with real-time validation",
    },
    {
      id: 46, // Asset Register
      title: "Asset Register",
      icon: <AssignmentOutlinedIcon sx={{ fontSize: 40, color: "#ff9800" }} />,
      path: `${buyerPathInitial}/Asset_Register`,
      description:
        "Track assets generated from GRNs with linked invoice and PO details.",
    },
    ...(isInventoryDesignation
      ? [
          {
            title: "Inventory Master & Warehouse",
            icon: (
              <InventoryOutlinedIcon sx={{ fontSize: 40, color: "#0f766e" }} />
            ),
            path: `${buyerPathInitial}/Inventory_Master`,
            description:
              "Maintain warehouses, configure stock, post material movements, and review ledger and consumption reports.",
          },
        ]
      : []),
    {
      id: 16, // ✅ Invoice Automation
      title: "Trade Claims Automation",
      icon: <ReceiptOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />,
      path: `${buyerPathInitial}/Invoice`,
      description:
        "Auto-generate, e-sign, validate, and bulk invoice approval workflows with seamless ERP/payment integration",
    },
    {
      title: "Invoice Reconciliation",
      icon: <MemorySharpIcon sx={{ fontSize: 40, color: "#00c3ffff" }} />,
      path: `${buyerPathInitial}/InvoiceReconciliation`,
      description:
        "Review buyer-side invoice reconciliation with backend search, pagination, and line-level matching details.",
    },
    {
      id: 7, // ✅ Master Records
      title: "Master Records",
      icon: <InventoryOutlinedIcon sx={{ fontSize: 40, color: "#8e24aa" }} />,
      path: `${buyerPathInitial}/Items`,
      description:
        "Centralized, validated, and auto-updated Item and Vendor master records for accurate procurement and reporting",
    },
    {
      id: 17, // ✅ Three Way Matching
      title: "Accounts Payable Automation",
      icon: <JoinInnerIcon sx={{ fontSize: 40, color: "#d81095d8" }} />,
      path: `${buyerPathInitial}/Matching`,
      description:
        "Cross-verifies three critical documents—Invoice, Purchase Order, and Receiving Document—for financial accuracy.",
    },
    {
      id: 49,
      title: "Invoice Automation - Services Payment",
      icon: (
        <AssignmentTurnedInOutlinedIcon
          sx={{ fontSize: 40, color: "#0f766e" }}
        />
      ),
      path: `${buyerPathInitial}/Service_Matching`,
      description:
        "Matches SOW or contract terms with approved timesheets for service billing validation.",
    },
    {
      id: 48,
      title: "Invoice Automation - Sorting & Matching",
      icon: <JoinInnerIcon sx={{ fontSize: 40, color: "#7c3aed" }} />,
      path: `${buyerPathInitial}/Invoice_PayAsYouGo`,
      description:
        "Replicates the P2P compliance and matching workflow with the same UI and functionality for a separate buyer module.",
    },
    // {
    //   id: 6,
    //   title: "Rate Contract",
    //   icon: (
    //     <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 40, color: "#00acc1" }} />
    //   ),
    //   path: `${buyerPathInitial}/rate_contract`,
    //   description: "Convert approved rate contract items into a Purchase Order for procurement.",
    // },

    {
      id: 47,
      title: "Rate Contract",
      icon: (
        <AssignmentTurnedInOutlinedIcon
          sx={{ fontSize: 40, color: "#00acc1" }}
        />
      ),
      path: `${buyerPathInitial}/rate_contract`,
      description:
        "Convert approved rate contract items into a Purchase Order for procurement.",
    },
    {
      title: "Price List",
      icon: (
        <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#7c3aed" }} />
      ),
      path: `${buyerPathInitial}/price_list`,
      description:
        "Upload price catalogs and generate quotations from price list data.",
    },
    {
      id: 45,
      title: "Sourcing Intelligence",
      icon: <AssessmentOutlinedIcon sx={{ fontSize: 40, color: "#2563eb" }} />,
      path: `${buyerPathInitial}/Sourcing_Intelligence`,
      description:
        "Price, vendor performance, risk signals, and procurement recommendations in one view.",
    },
    {
      id: 1,
      title: "Procurement Command Center",
      icon: <DashboardOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
      path: `${buyerPathInitial}/Dashboard`,
      description:
        "A real-time command center for tracking procurement activities, insights, and approvals",
    },
  ];

  const userDashboard = [
    {
      id: 34,
      title: "Purchase Requisition",
      icon: <NoteAddOutlinedIcon sx={{ fontSize: 40, color: "#e91e63" }} />,
      path: `${userPathInitial}/PR`,
      description:
        "Submit, Review, and Approve requests before initiating procurement",
    },
    ...(data?.user?.designation === "Invoice Analyst" ||
    data?.user?.designation === "Finance Analyst"
      ? [
          {
            title: "Trade Claims Automation",
            icon: (
              <ReceiptOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />
            ),
            path: `${userPathInitial}/Invoice`,
            description:
              "Auto-generate, e-sign, validate, and bulk invoice approval workflows with seamless ERP/payment integration",
          },
        ]
      : []),

    ...(data?.user?.designation === "Account Payable" ||
    data?.user?.designation === "Business Finance"
      ? [
          {
            title: "Invoice",
            icon: (
              <PriceCheckOutlinedIcon sx={{ fontSize: 40, color: "#ff9800" }} />
            ),
            path: `${userPathInitial}/ApproveInvoice`,
            description: "Approve and process invoices",
          },
          {
            title: "Invoice Reconciliation",
            icon: <MemorySharpIcon sx={{ fontSize: 40, color: "#00c3ffff" }} />,
            path: `${userPathInitial}/InvoiceReconciliation`,
            description:
              "Review matched invoices and approve/reject with remarks (Business Finance → Account Payable).",
          },
        ]
      : []),
    {
      id: 36,
      title: "Purchase Orders",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      path: `${userPathInitial}/PO`,
      description:
        "Automatically generate PO from approved quotations and selected vendors—ensuring speed and accuracy",
    },
    {
      id: 35,
      title: "Accounts Payable Automation",
      icon: <JoinInnerIcon sx={{ fontSize: 40, color: "#d81095d8" }} />,
      path: `${userPathInitial}/Matching`,
      description:
        "Cross-verifies three critical documents-Invoice, Purchase Order, and Receiving Document-for financial accuracy.",
    },
    // (data?.user?.designation === 'Procurement' || data?.user?.department === 'Procurement') && {
    //   title: "Onboarding Approval",
    //   icon: <CreditScoreOutlined sx={{ fontSize: 40, color: "#273fb0" }} />,
    //   path: `${userPathInitial}/vendor_approval`,
    //   description: "Review and approve vendor onboarding submissions to ensure compliance, authenticity, and readiness for procurement operations.",
    // },

    ...(data?.user?.designation === "Procurement" ||
    data?.user?.department === "Procurement"
      ? [
          {
            title: "Onboarding Approval",
            icon: (
              <CreditScoreOutlined sx={{ fontSize: 40, color: "#273fb0" }} />
            ),
            path: `${userPathInitial}/vendor_approval`,
            description:
              "Review and approve vendor onboarding submissions to ensure compliance, authenticity, and readiness for procurement operations.",
          },
        ]
      : []),

    ...(data?.user?.designation === "Store Assistant" || data?.user?.designation === "Store Executive" || data?.user?.designation === "Store Manager"
      ? [
          {
            id: 37,
            title: "GRN Automation",
            icon: (
              <ChecklistOutlinedIcon sx={{ fontSize: 40, color: "#00acc1" }} />
            ),
            path: `${grnpath}/GRN`,
            description:
              "Auto-generate Goods Receipt Notes from supplier invoices with real-time validation",
          },
        ]
      : []),

    ...(isInventoryDesignation
      ? [
          {
            title: "Inventory Master & Warehouse",
            icon: (
              <InventoryOutlinedIcon sx={{ fontSize: 40, color: "#0f766e" }} />
            ),
            path: `${userPathInitial}/Inventory_Master`,
            description:
              "Maintain warehouses, configure stock, post material movements, and review ledger and consumption reports.",
          },
        ]
      : []),

    {
      id: 33,
      title: "Procurement Command Center",
      icon: <DashboardOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
      path: `${userPathInitial}/Dashboard`,
      description:
        "A real-time command center for tracking procurement activities, insights, and approvals",
    },
  ];

  const vendorAdminDashboard = [
    {
      title: "Supplier Bid Submission",
      icon: (
        <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#4caf50" }} />
      ),
      path: `${vendorPathInitial}/RFQ`,
      description: "Submit bids for RFQs",
    },
    {
      title: "Customer PO",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      path: `${vendorPathInitial}/PO`,
      description: "View and track purchase orders",
    },
    {
      title: "Merchant Invoice ",
      icon: <ReceiptOutlinedIcon sx={{ fontSize: 40, color: "#ff9800" }} />,
      path: `${vendorPathInitial}/Vendor Invoice`,
      description: "Create and manage invoices",
    },

    {
      title: "Invoice Reconciliation",
      icon: <MemorySharpIcon sx={{ fontSize: 40, color: "#00c3ffff" }} />,
      path: `${vendorPathInitial}/InvoiceProcess`,
      description:
        "Invoices uploaded by vendors are validated and processed through GRN automation.",
    },

    {
      title: "Supplier User",
      icon: (
        <PersonOutlineOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />
      ),
      path: `${vendorPathInitial}/Users`,
      description: "Manage vendor user accounts",
    },
    {
      title: "Customer Command Center",
      icon: <DashboardOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
      path: `${vendorPathInitial}/Dashboard`,
      description:
        "A real-time command center for tracking procurement activities, insights, and approvals.",
    },
  ];

  const vendorContactDashboard = [
    {
      title: "Supplier Bid Submission",
      icon: (
        <RequestQuoteOutlinedIcon sx={{ fontSize: 40, color: "#f44336" }} />
      ),
      path: `${vendorPathInitial}/RFQ`,
      description: "Submit bids for RFQs",
    },
    {
      title: "Customer PO",
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 40, color: "#9c27b0" }} />,
      path: `${vendorPathInitial}/PO`,
      description: "View and track purchase orders",
    },

    {
      title: "Customer Command Center",
      icon: <DashboardOutlinedIcon sx={{ fontSize: 40, color: "#3f51b5" }} />,
      path: `${vendorPathInitial}/Dashboard`,
      description: "View vendor overview and analytics",
    },
  ];

  // Determine which dashboard to show based on user role
  const getDashboardItems = () => {
    if (roles.SuperAdmin) return superadminDashboard;
    if (roles.Admin) return orgDashboard;
    if (roles.Buyer) return buyerDashboard;
    if (roles.User) return userDashboard;
    if (roles.VendorAdmin) return vendorAdminDashboard;
    if (roles.VendorContact) return vendorContactDashboard;
    if (roles.UnregisteredVendor) return unregisteredVendorDashboard;
    return [];
  };

  const dashboardItems = getDashboardItems();
  const userName =
    data?.userName ||
    data?.name ||
    data?.user?.userName ||
    data?.user?.name ||
    data?.user?.first_name ||
    data?.user?.email ||
    "User";
  const roleLabel =
    data?.access_type ||
    data?.user?.access_type ||
    data?.user_type ||
    data?.user?.user_type ||
    data?.user?.role ||
    data?.user?.designation ||
    "Workspace";
  const formattedRoleLabel = String(roleLabel || "Workspace")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <Box className={styles.container}>
      {/* Header with Profile Card */}
     <Box className={styles.header}>
  <div className={styles.headerCopy}>
    <div className={styles.welcomeRow}>
      <Typography className={styles.welcomeText}>
        Welcome back, {userName} 👋
      </Typography>

      <div className={styles.roleBadge}>
        <span className={styles.roleDot}></span>
        {formattedRoleLabel} Workspace
      </div>
    </div>

    <Typography className={styles.workspaceText}>
      Manage your workspace, activities, and procurement tasks from here.
    </Typography>

    <div className={styles.dateRow}>
      <span className={styles.dateLabel}>Today</span>

      <Typography className={styles.dateText}>
        {currentDate}
      </Typography>
    </div>
  </div>

  <DashboardHeaderActions
    showDateBar={false}
    profileNode={
      roles.UnregisteredVendor ? (
        <UnregisteredVendorProfileCard profilePath={profilePath} />
      ) : (
        <ProfileCard
          profilePath={profilePath}
          vendorBtn={roles.VendorAdmin || roles.VendorContact}
          vendorBtnOutlined={roles.VendorAdmin || roles.VendorContact}
          buyerBtn={roles.Buyer || roles.User}
          buyerBtnOutline={roles.Buyer || roles.User}
        />
      )
    }
  />
</Box> 


      {/* Dashboard Cards Grid */}
      <Grid container spacing={3} className={styles.gridContainer}>
        

        {dashboardItems.map((item, index) => {
          const isDisabled =
            item.id !== undefined &&
            !ALWAYS_AVAILABLE_BUYER_MENU_IDS.includes(item.id) &&
            !normalizedAllowedMenu.includes(item.id);

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`dashboard-${index}`}>
              <Card
                className={`${styles.card} ${
                  isDisabled ? styles.cardDisabled : ""
                }`}
                onClick={() => {
                  if (!isDisabled) {
                    navigate(item.path);
                  }
                }}
                style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
              >
                <CardContent className={styles.cardContent}>
                  <Box className={styles.iconContainer}>{item.icon}</Box>
                  <Typography
                   
                    className={styles.cardTitle}
                    sx={{
                      fontFamily: "gilroyBold",
                      fontSize: { xs: "1.5rem", sm: "1.6rem", md: "1.8rem" },
                      
                     
                      
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    className={styles.cardDescription}
                    sx={{
                      fontFamily: "gilroyMedium",
                      fontSize: { xs: "1.4rem", sm: "1.45rem", md: "1.5rem" },
                      lineHeight: 1.7,
                    }}
                  >
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default LandingPage;
